import {
  clientErrorReportLimits,
  sanitizeClientErrorUrl,
} from "#src/utils/clientErrorReport.ts";
import { getToken } from "#src/utils/getToken.ts";
import type {
  reportClientError,
  reportClientErrorVariables,
} from "#types/code-generator.ts";
import type { TypedDocumentNode } from "#types/graphql.d.ts";
import type { DocumentNode } from "graphql";
import { gql } from "graphql-tag";

export const ReportClientErrorMutation = gql`
  mutation reportClientError($data: ReportClientErrorInput!) {
    reportClientError(data: $data) {
      success
    }
  }
` as TypedDocumentNode<reportClientError, reportClientErrorVariables>;

export async function reportClientError(
  error: unknown,
  componentStack?: string
) {
  try {
    const message = error instanceof Error ? error.message : String(error);

    if (
      message.includes("Failed to fetch dynamically imported module") ||
      message.includes("Importing a module script failed")
    ) {
      return false;
    }

    const limits = clientErrorReportLimits;
    const variables: reportClientErrorVariables = {
      data: {
        message: (message || "Unknown error").slice(0, limits.message),
        stack:
          error instanceof Error
            ? error.stack?.slice(0, limits.stack)
            : undefined,
        componentStack: componentStack?.slice(0, limits.componentStack),
        url: sanitizeClientErrorUrl(window.location.href).slice(0, limits.url),
        userAgent: navigator.userAgent.slice(0, limits.userAgent),
      },
    };
    const token = getToken();
    const query =
      typeof ReportClientErrorMutation === "string"
        ? ReportClientErrorMutation
        : (ReportClientErrorMutation as DocumentNode).loc.source.body;
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_APP_ENDPOINT}?op=reportClientError`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, variables }),
        signal: AbortSignal.timeout(8000),
      }
    );

    if (!response.ok) {
      return false;
    }
    const result = await response.json();
    return (
      !result.errors?.length && result.data?.reportClientError?.success === true
    );
  } catch {
    return false;
  }
}
